import { formatDistanceToNow } from 'date-fns';
import React from 'react';
import { PROCESSOR_COLORS, ProcessorType } from '../../types/processor';
import { BouncingDots } from './BouncingDots';

export interface ConversationMessage {
  id: number;
  agentName: string;
  stepId: string;
  processorType: ProcessorType;
  messageType: string;
  content: string;
  timestamp: string;
  indentLevel: number;
  isUserMessage?: boolean;
}

interface ProcessorMessageProps {
  message: ConversationMessage;
  isActive: boolean;
}

/**
 * Individual message bubble in the Conversation View (SWRM-164).
 *
 * Displays processor lifecycle events and LLM messages as chat-style bubbles
 * with processor-specific colors, indentation for sub-agents, and bouncing dots
 * for active processors.
 */
export const ProcessorMessage: React.FC<ProcessorMessageProps> = ({ message, isActive }) => {
  const colors = PROCESSOR_COLORS[message.processorType];
  const isFailed = message.messageType === 'processor_failed' || message.messageType === 'error';
  const isHumanInputRequest = message.content.includes('USER INPUT REQUESTED');
  const isProcessorComplete = message.messageType === 'processor_complete';

  // Calculate indent padding
  const indentPadding = message.indentLevel * 40;

  // Extract hex color from centralized PROCESSOR_COLORS for avatars and bouncing dots
  // Map Tailwind color classes to hex values (for components that need hex)
  const borderToHex: Record<string, string> = {
    'border-blue-500': '#3b82f6',
    'border-purple-500': '#a855f7',
    'border-yellow-500': '#eab308',
    'border-green-500': '#22c55e',
    'border-teal-500': '#14b8a6',
  };
  const borderToBg: Record<string, string> = {
    'border-blue-500': 'bg-blue-500',
    'border-purple-500': 'bg-purple-500',
    'border-yellow-500': 'bg-yellow-500',
    'border-green-500': 'bg-green-500',
    'border-teal-500': 'bg-teal-500',
  };

  const dotColor = borderToHex[colors.border] || '#6b7280';
  const avatarBgClass = borderToBg[colors.border] || 'bg-gray-500';

  // Format timestamp
  // Backend sends timestamps in UTC (no timezone suffix), so we need to append 'Z' to parse correctly
  const parseTimestamp = (timestamp: string): Date => {
    // If timestamp doesn't end with 'Z' or timezone offset, assume UTC and append 'Z'
    if (!timestamp.match(/[Z+-]\d{2}:\d{2}$/i)) {
      return new Date(timestamp + 'Z');
    }
    return new Date(timestamp);
  };
  
  const parsedTime = parseTimestamp(message.timestamp);
  const relativeTime = formatDistanceToNow(parsedTime, { addSuffix: true });
  const absoluteTime = parsedTime.toLocaleString();

  // Extract initials from agent name (remove [step_id] part first)
  const cleanAgentName = message.agentName.replace(/\s*\[.*?\]$/, '');
  const getInitials = (name: string): string => {
    // Take first 2-3 characters or first letters of words
    const words = name.split(' ');
    if (words.length >= 2) {
      return words.slice(0, 2).map(w => w[0]).join('').toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };
  const initials = getInitials(cleanAgentName);

  // User messages (human input responses) are right-aligned
  if (message.isUserMessage) {
    return (
      <div className="flex justify-end mb-4" style={{ paddingLeft: `${indentPadding}px` }}>
        <div className="max-w-[70%]">
          <div className="flex items-center justify-end gap-2 mb-1">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">👤 You</span>
            <span
              className="text-xs text-gray-500 dark:text-gray-400"
              title={absoluteTime}
            >
              {relativeTime}
            </span>
          </div>
          <div className="bg-orange-500 text-white rounded-2xl px-4 py-3 shadow-sm">
            <p className="text-sm whitespace-pre-wrap">{message.content}</p>
          </div>
        </div>
      </div>
    );
  }

  // Processor messages are left-aligned
  return (
    <div className="mb-4" style={{ paddingLeft: `${indentPadding}px` }}>
      <div className="flex items-start gap-3">
        {/* Processor avatar with initials - filled circle with contrasting text */}
        <div className="flex-shrink-0 mt-1">
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center shadow-sm ${avatarBgClass}`}
          >
            <span className="text-xs font-bold text-white">
              {initials}
            </span>
          </div>
        </div>

        {/* Message content */}
        <div className="flex-1 min-w-0">
          {/* Header with agent name and timestamp */}
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-sm font-semibold ${colors.text}`}>
              {cleanAgentName}
            </span>
            <span
              className="text-xs text-gray-500 dark:text-gray-400 cursor-help"
              title={absoluteTime}
            >
              {relativeTime}
            </span>
          </div>

          {/* Message bubble */}
          <div
            className={`${
              isProcessorComplete 
                ? 'bg-gray-100 dark:bg-gray-800' 
                : colors.bg
            } rounded-2xl px-4 py-3 shadow-sm ${
              isFailed ? 'ring-2 ring-red-500 dark:ring-red-400' : 
              isHumanInputRequest ? 'ring-2 ring-orange-500 dark:ring-orange-400' : ''
            }`}
          >
            {/* Show bouncing dots if active AND not a completion/failed message - but NOT if message contains USER INPUT REQUESTED */}
            {isActive && 
             message.messageType !== 'processor_complete' && 
             message.messageType !== 'processor_failed' && 
             !isHumanInputRequest ? (
              <div className="flex items-center gap-3">
                <BouncingDots color={dotColor} size="md" />
              </div>
            ) : (
              /* Message content */
              <div className={`text-sm ${
                isProcessorComplete 
                  ? 'text-gray-600 dark:text-gray-400' 
                  : colors.text
              }`}>
                {isFailed && <span className="mr-2">❌</span>}
                {isHumanInputRequest && <span className="mr-2">🤔</span>}
                {isProcessorComplete && <span className="mr-2">✓</span>}
                <span className="whitespace-pre-wrap">{message.content}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

